// angleLock.js — constrain a drawn segment to fixed bearings (0/45/90/…/315).
//
// Held-modifier behaviour, the CAD/Illustrator convention: while the modifier is down the next
// point is projected onto the nearest allowed bearing from the previous point, and a preview line
// shows exactly where it will land before the click commits it.
//
// COORDINATE NOTE: image space has y pointing DOWN. The maths below works directly in that space,
// but `bearingDeg` negates dy so the number shown to the operator reads the way they expect —
// 0 = right/east, 90 = straight up, 180 = left, 270 = down.
//
//   [ADR: docs/20260819_docking_path_types.html]

export const ANGLE_STEP_DEG = 45;

/**
 * Project `cursor` onto the nearest bearing from `anchor`, preserving the cursor's distance.
 * Distance is preserved (rather than, say, projecting perpendicularly) so the operator still
 * controls segment length freely while the modifier only governs direction.
 */
export function constrainToAngle(anchor, cursor, stepDeg = ANGLE_STEP_DEG) {
  if (!anchor || !cursor) return cursor || null;
  const dx = cursor[0] - anchor[0];
  const dy = cursor[1] - anchor[1];
  const len = Math.hypot(dx, dy);
  if (len < 1e-9) return [anchor[0], anchor[1]];
  const step = (stepDeg * Math.PI) / 180;
  const snapped = Math.round(Math.atan2(dy, dx) / step) * step;
  return [anchor[0] + Math.cos(snapped) * len, anchor[1] + Math.sin(snapped) * len];
}

/** Bearing from `anchor` to `p` in operator-facing degrees: 0 = right, 90 = up, CCW positive. */
export function bearingDeg(anchor, p) {
  if (!anchor || !p) return null;
  const deg = (Math.atan2(-(p[1] - anchor[1]), p[0] - anchor[0]) * 180) / Math.PI;
  return (deg + 360) % 360;
}

/** Distance in image px between two points. */
export function lengthPx(a, b) {
  if (!a || !b) return 0;
  return Math.hypot(b[0] - a[0], b[1] - a[1]);
}

/** True if this keyboard/mouse event carries the angle-lock modifier.
 *
 *  Shift is the primary: it is the convention in every drawing tool, and it is safe to hold while
 *  clicking. Meta/Cmd is also accepted because it is what was asked for — but on macOS Cmd+click
 *  is intercepted by the browser for other purposes, so Shift is the one to reach for. */
export function hasAngleLockModifier(e) {
  return !!e && (e.shiftKey === true || e.metaKey === true);
}
